import { useState } from 'react';
import { useNavigate } from 'react-router';
import style from './style.module.css';
import { ColumnsType } from 'antd/lib/table/interface';
import { Card, Col } from 'antd';
import { ItemType } from 'antd/lib/menu/hooks/useItems';
import { Empty, FilterIcon, Row, Table, Button, Menu, Dropdown } from 'fave-ui';
import { RenderColumn } from '../../../helpers/tableHelpers/RenderColumn';
import {
  createOnTableChangeHandler,
  formatFilter,
} from '../../../utils/utilFunctions';
import {
  FLPCashbackListingItem,
  useFetchCashbackCampaignListing,
} from '../../../services/CashbackCampaign/useFetchFLPCashbackListing';
import { defaultTableConfig } from '../../../helpers/defaults';
import { TableSettingsType } from '../../../types/configTypes';
import { Plus, DotsThree } from 'phosphor-react';
import { Link } from 'react-router-dom';
import { SearchBarExpandable } from '../../../components/common/SearchBarExpandable/SearchBarExpandable';
import { capitalizeFirstLetterEachWord } from '../../../utils/utilFunctions';
import { CampaignStatusType } from '../../../types/dataTypes';

const ECardGiveawayView = () => {
  const [searchQuery, setSearchQuery] = useState('');
  const [tableConfig, setTableConfig] =
    useState<TableSettingsType>(defaultTableConfig);
  const navigate = useNavigate();

  const { data: campaignData, isFetching: campaignFetching } =
    useFetchCashbackCampaignListing({
      page: tableConfig.page,
      limit: tableConfig.limit,
      sort_order: tableConfig.sort_order,
      sort_by: tableConfig.sort_by,
      filter: `campaign_name_id=${searchQuery}|${tableConfig.filter ?? ''}`,
      funder_type: 'BKE::ECard',
    });

  const campaignListingData = campaignData?.cashback_campaigns?.map(
    (campaign: FLPCashbackListingItem) => {
      return {
        ...campaign,
        status: capitalizeFirstLetterEachWord(
          campaign.status,
        ) as CampaignStatusType,
        available_budget_amount_cents:
          campaign.available_budget_amount_cents / 100,
      };
    },
  );

  const tableChangeHandler = createOnTableChangeHandler(
    setTableConfig,
    filters => formatFilter([{ key: 'status', value: filters.status }]),
  );

  const campaignListingColumns: ColumnsType<FLPCashbackListingItem> = [
    {
      title: 'ID',
      dataIndex: 'id',
      width: 80,
      render: RenderColumn.rowText,
    },
    {
      title: 'Campaign Name',
      dataIndex: 'campaign_name',
      width: 250,
      render: RenderColumn.rowText,
    },
    {
      title: 'Start Date',
      dataIndex: 'start_date',
      width: 200,
      render: RenderColumn.rowText,
    },
    {
      title: 'End Date',
      dataIndex: 'end_date',
      width: 200,
      render: RenderColumn.rowText,
    },
    {
      title: 'Status',
      dataIndex: 'status',
      filterIcon: FilterIcon,
      filters: [
        { text: `Active`, value: 'active' },
        { text: `Inactive`, value: 'inactive' },
        { text: `Archived`, value: 'archived' },
      ],
      width: 120,
      render: RenderColumn.campaignStatusTag,
    },
    {
      title: 'Change Log',
      width: 120,
      render: (row: FLPCashbackListingItem) => (
        <Link to={`changelog/${row.id}`}>
          <Button type={'link'}>View</Button>
        </Link>
      ),
    },
    {
      title: 'Actions',
      key: 'action',
      width: 120,
      render: (row: FLPCashbackListingItem) => {
        const actionItems: ItemType[] = [
          {
            key: 'edit',
            label: row.status !== 'Archived' ? 'View/Edit' : 'View',
            onClick: () =>
              navigate(
                `/admin/merchandise_and_campaign/cashback_campaigns/edit_ecard_campaign/${row.id}`,
              ),
          },
        ];

        const menu = <Menu items={actionItems} />;

        return (
          <Dropdown overlay={menu} trigger={['click']}>
            <Button
              type={'link'}
              className={style.btnText}
              onClick={e => e.preventDefault()}
            >
              <DotsThree size={32} />
            </Button>
          </Dropdown>
        );
      },
    },
  ];

  return (
    <>
      <Card style={{ width: '100%' }}>
        <Row className={style.summaryWrapper}>
          <Col>
            <h2 className={style.h2}>{'Campaign Summary'}</h2>
          </Col>
          <Col className={style.actionsCol}>
            <Row>
              <SearchBarExpandable
                onSearch={keyword => setSearchQuery(keyword.toLowerCase())}
              />
              <Button
                icon={<Plus className="icon-start" size={16} />}
                iconPlacement="start"
                onClick={() =>
                  navigate(
                    '/admin/merchandise_and_campaign/cashback_campaigns/new_campaign',
                  )
                }
                type="primary"
              >
                Create
              </Button>
            </Row>
          </Col>
        </Row>
      </Card>
      <Row>
        <Table
          size="large"
          rowKey={row => row.id}
          loading={campaignFetching}
          columns={campaignListingColumns}
          onChange={tableChangeHandler}
          dataSource={campaignListingData}
          className={style.table}
          pagination={{
            total: campaignData?.count,
            position: ['bottomCenter'],
            pageSize: tableConfig.pageSize,
            current: tableConfig.current,
            showSizeChanger: false,
          }}
          locale={{
            emptyText: !campaignFetching && (
              <Empty emptyType="cant-find-anything" />
            ),
          }}
        />
      </Row>
    </>
  );
};

export default ECardGiveawayView;
